/**
 * CMS Manager
 *
 * Editor-specific orchestration for CMS Scenes: bootstraps a Scene into the
 * viewer and tracks publish state for the Publish button.
 *
 * The cross-consumer scene-load primitives (`loadCmsScene`, URL/display-name
 * helpers) live in `trace-viewer/cms` so viewer-app and TraceFrontend can
 * share them. This class is the editor-only layer on top.
 *
 * **Folder mode** (CONTENT_MODEL_SPEC §2). A scene is a folder: one glTF 2.1
 * document at `trace-content/scenes/{id}/<hash>.gltf` plus the files it names
 * relatively under `assets/`. Saving writes the document; the files went up
 * one at a time as they were imported (`POST /scenes/{id}/files`), so a save
 * is KBs of JSON however large the scene is. See `src/cms/scene-folder.ts`.
 *
 * Publish is edit-in-place: a POST to `/contents` for `(sceneId,
 * 'RootContent')` replaces the existing row's latest version. Image-anchor
 * orientation lives on the Scene's `SceneAccessor` (not in /contents) and is
 * published via a separate PATCH `/scenes/{id}/accessors/{accessorId}`.
 *
 * There is one content surface. The editor-managed `"References"` node and its
 * `ReferenceContent` row are gone: the backend refuses that content type now
 * (TraceBackend #915), and a library reference is an ordinary node whose `src`
 * is an asset file URL (spec §3.4) — it lives in the document with everything
 * else, and needs no second row, no reserved node name and no second snapshot.
 */

import type { TraceViewerElement } from 'trace-viewer';
import { type SceneFolder, type SceneObject, type TraceScene, type UnpackFolderTarget } from 'trace-viewer/scene';
import { ImageAnchor, AnchorOrientation } from 'trace-viewer/behaviors';
import { snapshotExportData, validatePublishableScene, documentRoot, structuralRootRefusal } from 'trace-viewer/editor';
import {
    loadCmsScene,
    findRootContent,
    contentDownloadUrl,
    findImageAccessor,
    isSceneAnchored,
    buildCmsSceneInput,
    AccessType,
    AnchorType,
    Orientation as CmsOrientation,
    type SceneAccessorResponseDto,
    type SceneAccessorRequestDto,
} from 'trace-viewer/cms';
import { CmsClient, parseCmsParams, type TokenGetter } from './CmsClient';
import { copyForInsideEdit, needsReferenceCopy } from './reference-edit-guard';
import { adoptDocumentRoot } from './document-root';
import {
    computePublishPlan,
    type PublishSnapshots,
} from './scene-contents';
import {
    registerFolderFileUrl,
    saveFolder,
    sceneFolderBaseUrl,
    sceneManifestOptions,
} from './scene-folder';
import { IS_FOLDER_STORAGE } from '../config/contentStorage';
import { editorExclusion } from '../editor-content';

export { parseCmsParams };

// ── Types ────────────────────────────────────────────────────────────

/**
 * Format for the RootContent surface of a CMS Scene.
 *
 * - `'gltf'` — the folder document (CONTENT_MODEL_SPEC §2.1): glTF 2.1 JSON
 *   naming its files relatively, uploaded beside them in the scene's folder.
 *   The only format the editor writes.
 * - `'zip'` — the pre-content-model bundled archive (models.json +
 *   info.json + assets/), which the Unity app and the other legacy clients
 *   read. Still offered from the export menu, and what a LEGACY-mode build
 *   publishes by default.
 *
 * Which one a save with no explicit format picks follows the build's content
 * write mode (`VITE_CONTENT_STORAGE`, `src/config/contentStorage.ts`) — see
 * {@link DEFAULT_ROOT_FORMAT}. Readers accept both shapes in either mode.
 *
 * There is no `'glxf'`: glXF still **reads** (the loader normalizes it), but
 * nothing writes it any more (spec §5 — readers accept both shapes, writers
 * switch once).
 */
export type RootContentFormat = 'gltf' | 'zip';

export interface PublishResult {
    rootPublished: boolean;
    /** True when the image-anchor orientation was PATCHed onto the CMS Scene accessor. */
    anchorPublished: boolean;
    /** Per-surface failure messages (empty on full success). */
    failures: string[];
}

/**
 * The format a save picks when the caller does not name one — the headless
 * `save_scene` tool, the editor's Ctrl+S / save command, anything routed
 * through `TraceSandbox.saveForAgent`.
 *
 * It FOLLOWS the build's content write mode (CONTENT_MODEL_SPEC §6.1): a
 * legacy build publishes the `.zip` the pre-content-model editor published, a
 * folder build publishes the `.gltf` document. Legacy is the default, so a
 * deployed sandbox keeps working against an API that has no content-model
 * endpoints and keeps the Unity app reading what it wrote.
 *
 * The two explicit “Publish as .zip” / “Publish as .gltf” menu items pass
 * `rootFormat` themselves and are unaffected — they are the author choosing,
 * which always beats the build default.
 */
export const DEFAULT_ROOT_FORMAT: RootContentFormat = IS_FOLDER_STORAGE ? 'gltf' : 'zip';

export interface PublishSceneOptions {
    /**
     * RootContent format. Defaults to {@link DEFAULT_ROOT_FORMAT}, which
     * follows `VITE_CONTENT_STORAGE` — `'zip'` in legacy mode (the default),
     * `'gltf'` in folder mode.
     */
    rootFormat?: RootContentFormat;
}

/** One-line summary for toast display: lists what published and what failed. */
export function buildPublishSummary(result: PublishResult): string {
    const published: string[] = [];
    if (result.rootPublished) published.push('root');
    if (result.anchorPublished) published.push('anchor');

    const parts: string[] = [];
    if (published.length > 0) {
        parts.push(`Published ${published.join(' + ')}`);
    }
    if (result.failures.length > 0) {
        parts.push(`Failed: ${result.failures.join('; ')}`);
    }
    return parts.length > 0 ? parts.join(' · ') : 'Already up to date';
}

// ── Factory ─────────────────────────────────────────────────────────

/**
 * Create a CmsManager from URL query string params and a token getter.
 * Returns null if the URL doesn't reference a CMS scene.
 */
export function createCmsManagerFromUrl(search: string, getToken: TokenGetter): CmsManager | null {
    const cmsParams = parseCmsParams(search);
    if (!cmsParams) return null;

    const params = new URLSearchParams(search);
    const blobBaseUrl = params.get('blobUrl') ?? (import.meta.env.VITE_BLOB_URL as string | undefined);
    if (!blobBaseUrl) {
        console.error('[CmsManager] blobUrl param or VITE_BLOB_URL environment variable is required for CMS mode');
        return null;
    }

    const client = new CmsClient(getToken, cmsParams.apiBaseUrl, blobBaseUrl);
    return new CmsManager(client, cmsParams.sceneId, blobBaseUrl);
}

// ── Manager ─────────────────────────────────────────────────────────

/**
 * Derive the RootContent format from its download URL's extension (query
 * string/fragment stripped). Null for anything that isn't one of the two
 * formats the editor can write (a legacy `.trace` or `.glxf` root reads
 * fine and simply republishes as `.gltf`).
 *
 * Exported for testing.
 */
export function rootFormatFromUrl(url: string): RootContentFormat | null {
    const path = url.split(/[?#]/, 1)[0];
    const dot = path.lastIndexOf('.');
    const ext = dot >= 0 ? path.slice(dot + 1).toLowerCase() : '';
    return ext === 'gltf' || ext === 'zip' ? ext : null;
}

/** Whether `ancestor` is in `node`'s parent chain. False for `node === ancestor`. */
function hasAncestor(node: SceneObject, ancestor: SceneObject): boolean {
    let walk: SceneObject | null = node.getParent();
    while (walk) {
        if (walk === ancestor) return true;
        walk = walk.getParent();
    }
    return false;
}

export class CmsManager {
    private _client: CmsClient;
    private _sceneId: string;
    private _blobBaseUrl: string;

    /** Snapshot taken at last publish (or initial load) for dirty detection. */
    private _lastSnapshots: PublishSnapshots = { root: null };
    /**
     * Absolute URL of the `RootContent` document that is currently stored, so
     * the scene's folder can be addressed by the directory the document sits
     * in rather than by re-deriving a container path. Null before the first
     * save of a new scene — {@link folderBaseUrl} falls back then.
     */
    private _rootContentUrl: string | null = null;
    /**
     * True once a Scene's RootContent has been successfully loaded. False when
     * the Scene has no Root row (see {@link _rootMissing}) or the Root row
     * failed to load (e.g. malformed archive). A Publish on a *broken* Root
     * would overwrite it with whatever's in the editor — refused. A Publish
     * on a *missing* Root is the first publish for a new scene — allowed.
     */
    private _rootLoaded = false;
    /**
     * True when the Scene has no RootContent row at all (newly created scene).
     * Distinct from `_rootLoaded === false` due to a load failure — see
     * {@link rootMissing}. Allows the first publish to create the Root row.
     */
    private _rootMissing = false;
    /**
     * Format of the RootContent currently stored in the CMS: derived from the
     * loaded Root row's URL on open, refreshed after each successful root
     * publish. Null when unknown — new scene (no row yet) or a legacy root in
     * neither format. Publishing with a different format re-uploads the root
     * even when the content snapshot is clean; null compares as "different"
     * so an unknown stored format never suppresses a requested one.
     */
    private _lastRootFormat: RootContentFormat | null = null;
    /**
     * In-flight load/attach promise. Awaited at the top of {@link publishScene}
     * so a publish click during the open sequence doesn't see stale snapshots
     * and treat both surfaces as dirty.
     */
    private _attachInFlight: Promise<void> | null = null;

    /**
     * Snapshot of the scene's image-marker SceneAccessor at load time, used as
     * the baseline for orientation dirty-detection. Null when the scene has no
     * image-marker accessor (e.g. a Location-only scene). Refreshed after a
     * successful accessor PATCH.
     */
    private _imageAccessorBaseline: SceneAccessorResponseDto | null = null;
    /**
     * Whether the scene is attached to an image-marker anchor, per the record
     * loaded in `_loadSceneInner` (`isSceneAnchored`). Declares the `.zip`
     * frame on publish; null (record never loaded) → viewer fallback. See
     * trace-viewer docs-internal/ZIP_FRAME_CONTRACT.md.
     */
    private _sceneAnchored: boolean | null = null;

    constructor(client: CmsClient, sceneId: string, blobBaseUrl: string) {
        this._client = client;
        this._sceneId = sceneId;
        this._blobBaseUrl = blobBaseUrl;
    }

    get client(): CmsClient { return this._client; }
    get sceneId(): string { return this._sceneId; }
    /** Whether the Scene's RootContent loaded successfully. */
    get rootLoaded(): boolean { return this._rootLoaded; }
    /**
     * True when the Scene has no RootContent row yet (new scene, first publish
     * will create it). Publish UI should be enabled on `rootLoaded || rootMissing`.
     */
    get rootMissing(): boolean { return this._rootMissing; }
    /**
     * Format of the RootContent currently stored in the CMS, null when
     * unknown (new scene, legacy root). Lets publish UI keep a format-switch
     * publish enabled even when the content itself is clean.
     */
    get lastRootFormat(): RootContentFormat | null { return this._lastRootFormat; }

    /**
     * Absolute URL of this scene's content folder, ending in `/`.
     *
     * A relative `assets/<hash>.<ext>` in the document resolves against this,
     * and it is what an eager import registers so a just-uploaded file still
     * resolves after the in-memory blob is gone.
     */
    get folderBaseUrl(): string {
        return sceneFolderBaseUrl(this._blobBaseUrl, this._sceneId, this._rootContentUrl);
    }

    /**
     * Import one file into this scene's folder and answer the relative URI the
     * document must reference it by (spec §2.6, first row).
     *
     * Called before the viewer imports the bytes, so the object can be created
     * with its final `src` — there is no second pass rewriting srcs at save
     * time, and nothing transient (`blob:`) ever reaches a document.
     */
    async importFile(file: Blob, fileName: string): Promise<string> {
        const entry = await this._client.uploadSceneFile(this._sceneId, file, fileName);
        return entry.uri;
    }

    /**
     * This scene's folder, as the uploader the viewer's unpack takes
     * ("Copy to scene", spec §2.6).
     *
     * The viewer never talks to an API: it hands over a `Blob` and takes back
     * the relative uri the document must name it by. That is exactly
     * {@link importFile}, so the freeze and an ordinary file import put bytes
     * in the folder the same single way.
     */
    folderUploadTarget(): UnpackFolderTarget {
        return { upload: (file: Blob, fileName: string) => this.importFile(file, fileName) };
    }

    /**
     * Register the folder URL a just-imported file resolves to, so the
     * reference survives the in-memory blob (a `clearScene`, a reload of the
     * behavior that owns it).
     */
    registerImportedFile(traceScene: TraceScene, uri: string): void {
        registerFolderFileUrl(traceScene.resources, this.folderBaseUrl, uri);
    }

    // ── Load ─────────────────────────────────────────────────────

    /**
     * Bootstrap a CMS Scene end-to-end:
     *   1. Open RootContent + image anchor via `loadCmsScene` (shared with viewer-app).
     *   2. Record the folder the document sits in.
     *   3. If Root loaded, snapshot the publishable surface for dirty detection.
     *      Otherwise skip snapshotting (and Publish UI is gated by callers on `rootLoaded`).
     */
    async loadScene(viewer: TraceViewerElement, onSceneLoaded: () => void): Promise<void> {
        const work = this._loadSceneInner(viewer, onSceneLoaded);
        this._attachInFlight = work.then(() => undefined, () => undefined);
        await work;
    }

    private async _loadSceneInner(viewer: TraceViewerElement, onSceneLoaded: () => void): Promise<void> {
        try {
            const [scene, contents] = await Promise.all([
                this._client.getScene(this._sceneId),
                this._client.listSceneContents(this._sceneId),
            ]);
            console.log(`[CmsManager] Scene has ${contents.length} content entries`);

            // Capture the image-marker accessor baseline before loading. The
            // ImageAnchor behavior is synthesized from this; publishScene uses
            // it to detect orientation changes.
            this._imageAccessorBaseline = findImageAccessor(scene.sceneAccessors) ?? null;
            this._sceneAnchored = isSceneAnchored(scene);

            // Shared predicate-gated flattening: an un-anchored scene yields an
            // empty input (its lingering imageUrl must not be honored).
            const loadInput = buildCmsSceneInput(scene);
            const result = await loadCmsScene(viewer, loadInput, contents, this._blobBaseUrl);
            this._rootLoaded = result.rootLoaded;
            this._rootMissing = result.rootMissing;
            // Record the stored root's format so publishScene can detect a
            // format switch on otherwise-clean content. Same row selection +
            // URL as loadCmsScene, so this reflects what actually loaded.
            const rootRow = result.rootLoaded ? findRootContent(contents) : null;
            const rootUrl = rootRow ? contentDownloadUrl(this._blobBaseUrl, rootRow) : null;
            this._rootContentUrl = rootUrl;
            this._lastRootFormat = rootUrl ? rootFormatFromUrl(rootUrl) : null;

            // Every session has a document root (D8): a document brings its
            // own, an empty scene (no Root row yet) and a legacy flat `.zip`
            // are adopted here, named for the scene. BEFORE the snapshot
            // baseline below, so a freshly opened scene is not dirty.
            await adoptDocumentRoot(viewer.traceScene, scene.name);

            onSceneLoaded();

            // Snapshot baseline is captured on either a successful Root load
            // (real content) or a missing Root (new scene: empty baseline →
            // first publish creates the row). It's skipped only when Root
            // *failed* to load — see `rootLoaded`.
            if (this._rootLoaded || this._rootMissing) {
                this._captureSnapshots(viewer);
            } else {
                console.warn('[CmsManager] Skipping snapshot baseline — RootContent did not load; Publish disabled');
            }

            // loadCmsScene frames the camera *tight on the marker* (for the
            // viewer-app's 2D→3D transition). After all content + references
            // are attached, re-frame the camera to fit the whole scene —
            // what an editor user expects when opening a scene to author it.
            // `frameScene()` preserves the camera's current alpha/beta and
            // only adjusts target/radius/clip planes.
            viewer.frameScene();

            // Install flat-scene reparent policy after load completes. The
            // loader itself may have parented children under non-root nodes
            // (inside a nested reference subtree, which is allowed) —
            // installing earlier would interfere with that. From here on,
            // every Inspector drag-drop or programmatic reparent inside the
            // scene root is vetoed in-place, matching the `.zip` publish-time
            // {@link validatePublishableScene} check.
            this._installFlatSceneReparentPolicy(viewer);
        } catch (error) {
            console.error('[CmsManager] Failed to load from CMS:', error);
            if (error instanceof Error && error.name === 'CmsAuthError') {
                alert(error.message);
            }
        }
    }

    // ── Publish ──────────────────────────────────────────────────

    /**
     * Omnibus publish: detects which surfaces (the scene document, the image
     * anchor) have changed since last publish/load and uploads only the dirty
     * ones. The document also counts as dirty when the requested `rootFormat`
     * differs from the format currently stored in the CMS (see
     * {@link _lastRootFormat}) — that is how a legacy `.zip` or `.glxf` row
     * gets rewritten as a folder document.
     *
     * Each surface publishes independently; a failure on one does not abort
     * the other. Snapshots are refreshed only for surfaces that succeeded.
     */
    async publishScene(
        viewer: TraceViewerElement,
        options: PublishSceneOptions = {},
    ): Promise<PublishResult> {
        const rootFormat: RootContentFormat = options.rootFormat ?? DEFAULT_ROOT_FORMAT;
        // If an open/attach is still in progress, wait for it so snapshots are
        // grounded against the just-loaded state — otherwise both surfaces would
        // appear dirty and we'd re-upload identical content.
        if (this._attachInFlight) {
            await this._attachInFlight;
        }

        const traceScene = viewer.traceScene;
        if (!traceScene) {
            return { rootPublished: false, anchorPublished: false, failures: ['Scene not initialized'] };
        }

        // Safety: refuse to publish if Root existed but failed to load. The
        // snapshot baseline is meaningless in that state — publishing would
        // overwrite the broken Root with whatever's currently in the scene
        // (likely empty or partial). A *missing* Root (new scene) is fine —
        // the first publish creates the row.
        if (!this._rootLoaded && !this._rootMissing) {
            return {
                rootPublished: false,
                anchorPublished: false,
                failures: ['RootContent did not load — cannot publish until the broken Root is replaced'],
            };
        }

        const current = this._currentSnapshots(traceScene);
        const plan = computePublishPlan(current, this._lastSnapshots);
        const anchorDirty = this._isAnchorOrientationDirty(traceScene);
        // The content snapshot can't see the wire format — a clean scene
        // republished in a different format must still upload, otherwise
        // switching .zip ↔ .gltf is a silent no-op.
        const rootDirty = plan.rootDirty || rootFormat !== this._lastRootFormat;
        const failures: string[] = [];
        let rootPublished = false;
        let anchorPublished = false;

        if (!rootDirty && !anchorDirty) {
            console.log('[CmsManager] Nothing to publish — all surfaces clean');
            return { rootPublished, anchorPublished, failures };
        }

        if (rootDirty) {
            if (!plan.rootDirty) {
                console.log(`[CmsManager] Root content unchanged, but format is ${this._lastRootFormat ?? 'unknown'} → republishing as .${rootFormat}`);
            }
            try {
                await this._publishRoot(viewer, rootFormat);
                this._lastSnapshots.root = current.root;
                this._lastRootFormat = rootFormat;
                // First publish on a missing-Root scene creates the row.
                this._rootMissing = false;
                this._rootLoaded = true;
                rootPublished = true;
            } catch (error) {
                const msg = error instanceof Error ? error.message : String(error);
                console.error('[CmsManager] Root publish failed:', error);
                failures.push(`Root: ${msg}`);
            }
        }

        if (anchorDirty) {
            try {
                await this._publishAnchorOrientation(traceScene);
                anchorPublished = true;
            } catch (error) {
                const msg = error instanceof Error ? error.message : String(error);
                console.error('[CmsManager] Anchor publish failed:', error);
                failures.push(`Anchor: ${msg}`);
            }
        }

        return { rootPublished, anchorPublished, failures };
    }

    // ── Private helpers ──────────────────────────────────────────

    /**
     * Install the flat-scene reparent policy.
     *
     * Rule, keyed on the *destination* (not the origin): if `newParent` is
     * inside the document root's tree, the move must land directly under the
     * document root — anything else inside that tree would create a nested
     * hierarchy. Destinations outside it (top-level orphans) are allowed, so a
     * user can move an object out of the publishable scene without fighting
     * the editor.
     *
     * The document root itself is the document (D8) and never moves; the
     * engine root is not a node at all. Both are refused with a log.
     *
     * The `.zip` publish gate ({@link validatePublishableScene}) backs this up
     * — it reads the same shape: one parentless record, every other node a
     * direct child of it. The live veto exists so the user sees the conflict
     * at drop time, not at publish time. (The folder publish is gated on its
     * manifest instead, see {@link _publishRoot}.)
     */
    private _installFlatSceneReparentPolicy(viewer: TraceViewerElement): void {
        const traceScene = viewer.traceScene;
        if (!traceScene) return;
        traceScene.setReparentPolicy((child, newParent) => {
            const docRoot = documentRoot(traceScene);
            if (!docRoot) return true;

            // A node moved OUT of a placed reference is an edit inside that
            // reference (SCENE_ROOT_SPEC §2.3 "Editing"; moving INTO one is
            // already vetoed by the viewer's `canParentUnder`). The policy is
            // synchronous and a copy is not, so the move is refused here and
            // re-issued once the asset has been copied into this scene — on
            // the same `SceneObject`, which survives the unpack.
            if (needsReferenceCopy(child)) {
                const destination = newParent;
                void copyForInsideEdit(child).then(copied => {
                    if (!copied) return;
                    // Re-issued through the same interceptor the drag used, so
                    // the flat-scene rule below still gets its say. A detach to
                    // the top level has no SceneObject to name as the parent.
                    if (destination) traceScene.reparentSceneObject(child, destination);
                    else child.transform.parent = null;
                });
                return false;
            }

            // Neither root moves: the document root IS the document and the
            // engine root is a runtime mount point (D8).
            const refusal = structuralRootRefusal(child, 'move');
            if (refusal) {
                console.warn(`[CmsManager] Blocked move — ${refusal}`);
                return false;
            }

            // Destinations outside the document root's tree are allowed and
            // keep their own hierarchy semantics. Top-level detach is also
            // allowed: the orphaned object simply leaves the publishable
            // scene without violating the flat invariant.
            const destInsideDocumentRoot = newParent === docRoot
                || (newParent !== null && hasAncestor(newParent, docRoot));
            if (!destInsideDocumentRoot) return true;

            // Inside the document root's tree → must land directly under it.
            if (newParent === docRoot) return true;
            console.warn(`[CmsManager] Blocked reparent of "${child.name}" — CMS Scenes must be flat (no nested parents)`);
            return false;
        });
    }

    /** Snapshot the scene's single publishable surface. */
    private _currentSnapshots(traceScene: TraceScene): PublishSnapshots {
        return {
            // Match what publish actually uploads (`getFullExportData`): the
            // document root + its behaviors are part of the payload (D8), so
            // edits to root-level behaviors must dirty the root snapshot too.
            root: snapshotExportData(traceScene.getFullExportData(editorExclusion(traceScene))),
        };
    }

    /** Capture and store baseline snapshots (called after load and after publish). */
    private _captureSnapshots(viewer: TraceViewerElement): void {
        const traceScene = viewer.traceScene;
        if (!traceScene) return;
        this._lastSnapshots = this._currentSnapshots(traceScene);
    }

    /**
     * Upload the current scene as RootContent.
     *
     * The two formats are gated differently, because they can carry
     * different things:
     *
     *   - `'gltf'` — the **folder** (spec §2.1): `exportSceneFolder` returns
     *     the glTF 2.1 document plus the files it names, the files go to
     *     `POST /scenes/{id}/files` and the document to `POST /contents`.
     *     Files the folder already holds are not sent: what the scene
     *     streams from it, imports (uploaded eagerly) and what an earlier
     *     save this session stored with the same bytes. Its gate is the
     *     manifest pre-flight inside the export (spec §2.5, wired through
     *     {@link sceneManifestOptions}): it classifies what was actually
     *     WRITTEN, where a relative `assets/…` uri is the normal case — a
     *     `builtin:` primitive, for one, is baked to a folder file by the
     *     export and must not be refused before it gets the chance.
     *   - `'zip'` — legacy bundled archive via
     *     `viewer.exportToBlob({ zipFormat: 'zip' })`; still offered while old
     *     scenes are migrated. A published `.zip` Scene may not bundle: it is
     *     flat and src-ref-only, so {@link validatePublishableScene} (the
     *     standalone-document rule) gates it before a byte is exported.
     *
     * Filename extension must match the blob bytes — the backend stores the
     * blob at `<id>.<ext>` from the name, and the loader dispatches by
     * extension.
     */
    private async _publishRoot(
        viewer: TraceViewerElement,
        format: RootContentFormat,
    ): Promise<void> {
        const traceScene = viewer.traceScene;
        if (!traceScene) {
            throw new Error('Scene not initialized');
        }

        if (format === 'gltf') {
            await this._publishFolder(viewer, traceScene);
            return;
        }

        const validation = validatePublishableScene(traceScene.getFullExportData(editorExclusion(traceScene)));
        if (!validation.ok) {
            throw new Error(validation.message);
        }

        const sceneName = traceScene.sceneName;
        // The scene's anchor decides the `.zip` frame — declare it from the
        // loaded record rather than letting the export fall back to racy
        // runtime state (ZIP_FRAME_CONTRACT.md).
        const blob = await viewer.exportToBlob({
            zipFormat: 'zip',
            ...editorExclusion(traceScene),
            ...(this._sceneAnchored !== null && { imageMarker: this._sceneAnchored }),
        });

        console.log(`[CmsManager] Uploading RootContent as .zip (${blob.size} bytes)…`);
        await this._client.uploadContent(this._sceneId, blob, {
            name: `${sceneName}.zip`,
            contentType: 'RootContent',
            version: '1.0',
        });
    }

    /**
     * Export the scene as a folder, letting the two ways that can fail reach
     * the user with the offending src in hand.
     *
     * Both are thrown before a byte is uploaded, and both name what is wrong:
     * the manifest pre-flight (spec §2.5) reports the entry index and the
     * class it refused, and an unresolvable relative src (trace-viewer #807)
     * reports every src it could not account for. Neither is recoverable
     * here and neither may be swallowed — writing the document anyway is a
     * save that deletes the model — so this only makes sure the whole detail
     * reaches the console, where it survives a toast that truncates it.
     */
    private async _exportFolder(viewer: TraceViewerElement, folderUrl: string): Promise<SceneFolder> {
        try {
            return await viewer.exportSceneFolder({
                manifest: sceneManifestOptions(this._client.apiBaseUrl, this._blobBaseUrl),
                ...editorExclusion(viewer.traceScene),
                destination: 'same',
                folderUrl,
            });
        } catch (error) {
            console.error(
                '[CmsManager] Folder export failed — nothing was uploaded and the stored scene is unchanged:',
                error,
            );
            throw error;
        }
    }

    /**
     * The folder-mode save (spec §2.1, §2.4, §2.6).
     *
     * 1. Export the folder with the **manifest pre-flight** on (spec §2.5).
     *    It classifies every `files[]` entry as a folder file, a library
     *    reference or foreign streamable media and throws with the offending
     *    index and class before a single byte is uploaded — the same three
     *    classes `ManifestValidator.cs` applies on the write, so a document
     *    that passes here is one the server will accept.
     * 2. {@link saveFolder} uploads the files the folder does not hold
     *    through trace-viewer's `uploadFolder` (shared with Studio), and
     *    retries a refused document once with the session's records
     *    forgotten.
     * 3. POST the document.
     */
    private async _publishFolder(viewer: TraceViewerElement, traceScene: TraceScene): Promise<void> {
        const folderUrl = this.folderBaseUrl;
        const name = `${traceScene.sceneName}.gltf`;
        const { folder, document, written: collection } = await saveFolder({
            folderUrl,
            resources: traceScene.resources,
            exportFolder: () => this._exportFolder(viewer, folderUrl),
            ownerId: this._sceneId,
            uploader: this._client,
            write: (blob) => this._client.uploadContent(this._sceneId, blob, {
                name,
                contentType: 'RootContent',
                version: '1.0',
            }),
        });
        console.log(
            `[CmsManager] Saved RootContent as .gltf (${document.size} bytes; `
            + `${folder.files.size} folder file(s) sent, ${folder.existing.size} already in the folder)`,
        );

        // The row's new url is the folder's document — and therefore the base
        // every relative uri resolves against for the rest of this session.
        const row = collection.contents?.find(c => c.contentType === 'RootContent');
        if (row) this._rootContentUrl = contentDownloadUrl(this._blobBaseUrl, row);
    }

    /**
     * The scene's CMS-managed ImageAnchor.
     *
     * It lands on the **document root** (SCENE_ROOT_SPEC §5.1, D8) — the root
     * entity of the loaded document. A legacy flat `.zip` scene states no root
     * node of its own, so its metadata stayed on the engine mount point and
     * the adoption that gave the session a document root did not move it;
     * that is the fallback, and it retires with the legacy readers.
     */
    private _imageAnchor(traceScene: TraceScene): ImageAnchor | null {
        return documentRoot(traceScene)?.getBehavior(ImageAnchor)
            ?? traceScene.getSceneRoot()?.getBehavior(ImageAnchor)
            ?? null;
    }

    /**
     * True when the live ImageAnchor orientation or pitch differs from the
     * load-time accessor baseline. False when there's no PATCH-able image
     * accessor (no baseline, no id), no ImageAnchor on the document root, or both
     * fields match.
     */
    private _isAnchorOrientationDirty(traceScene: TraceScene): boolean {
        const baseline = this._imageAccessorBaseline;
        // No id → can't PATCH, so a "dirty" verdict would just fail at publish.
        // Treat as clean here so the user isn't confronted with a meaningless
        // failure toast.
        if (!baseline?.id) return false;

        const anchor = this._imageAnchor(traceScene);
        if (!anchor) return false;

        // `AnchorOrientation` and CMS `Orientation` are distinct enum types
        // but share identical string values — compare as strings.
        const liveOrientation = anchor.orientation as string | undefined;
        const baselineOrientation = baseline.orientation as string | undefined | null;
        if ((liveOrientation ?? null) !== (baselineOrientation ?? null)) return true;

        const livePitch = anchor.pitchDegrees ?? 0;
        const baselinePitch = baseline.pitchDegrees ?? 0;
        return livePitch !== baselinePitch;
    }

    /**
     * PATCH the scene's image-marker SceneAccessor with the live ImageAnchor's
     * orientation and pitch. The endpoint requires the full request shape
     * (accessType, anchorType, etc.), so we splice the new fields into the
     * baseline. Refreshes the baseline on success so a subsequent publish is
     * a no-op.
     */
    private async _publishAnchorOrientation(traceScene: TraceScene): Promise<void> {
        const baseline = this._imageAccessorBaseline;
        if (!baseline?.id) {
            throw new Error('No image-marker accessor on this scene; cannot publish orientation');
        }

        const anchor = this._imageAnchor(traceScene);
        if (!anchor) {
            throw new Error('No ImageAnchor behavior on the document root');
        }

        // Reuse all of the baseline accessor's other fields; only orientation
        // and pitch change. accessType is required; an "Anywhere" accessor
        // accepts the orientation field unchanged, so this works regardless.
        // The Response DTO uses raw strings for these enums while the Request
        // DTO uses the typed enums — convert as we copy over.
        const newOrientation = anchor.orientation ?? AnchorOrientation.Free;
        const newPitchDegrees = anchor.pitchDegrees;
        const body: SceneAccessorRequestDto = {
            accessType: (baseline.accessType as AccessType) ?? AccessType.Anywhere,
            anchorType: (baseline.anchorType as AnchorType) ?? AnchorType.Image,
            orientation: newOrientation as unknown as CmsOrientation,
            pitchDegrees: newPitchDegrees,
            address: baseline.address ?? undefined,
            latitude: baseline.latitude ?? undefined,
            longitude: baseline.longitude ?? undefined,
        };

        console.log(`[CmsManager] PATCHing accessor ${baseline.id}: orientation=${newOrientation}, pitchDegrees=${newPitchDegrees}`);
        await this._client.updateSceneAccessor(this._sceneId, baseline.id, body);

        // Refresh the baseline so a subsequent publish call is clean.
        // `AnchorOrientation` and CMS `Orientation` are distinct enum types with
        // identical string values — the body sent over the wire carries a
        // string that the server stores, so we can store the same string back.
        this._imageAccessorBaseline = {
            ...baseline,
            orientation: newOrientation as unknown as CmsOrientation,
            pitchDegrees: newPitchDegrees,
        };
    }
}
